import {
  type PlatformAgent,
  PlatformConnection,
  type PlatformExecutionProfile,
  PlatformRunnerState,
  type ProfileScope,
} from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { apiJson, apiRequest } from "@/lib/api"

const path = (workspaceId: string) => `/api/workspaces/${encodeURIComponent(workspaceId)}/platform-runner`
export const platformQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "platform-runner"],
    queryFn: () => apiJson(path(workspaceId), PlatformRunnerState),
    // one retry: a hung or failing platform read shows its error and Retry, not a minute of loading (UX pass 3, C7)
    retry: 1,
    refetchInterval: (query) =>
      query.state.data?.connections.some((c) => ["starting", "authorize"].includes(c.state)) ? 1000 : 30_000,
  })
export const connectPlatform = (
  workspaceId: string,
  agent: PlatformAgent,
  profileId: string,
  runnerId?: string,
) =>
  apiJson(`${path(workspaceId)}/connect`, PlatformConnection, {
    method: "POST",
    body: { agent, profileId, runnerId },
  })
export const cancelPlatformConnection = (workspaceId: string, id: string) =>
  apiRequest(`${path(workspaceId)}/connections/${encodeURIComponent(id)}`, null, { method: "DELETE" })
export const importClaudeSubscription = (
  workspaceId: string,
  input: { profileId: string; token: string; createdAt: number | null },
) => apiRequest(`${path(workspaceId)}/subscriptions/claude`, null, { method: "PUT", body: input })
export const submitPlatformCode = (workspaceId: string, id: string, code: string) =>
  apiRequest(`${path(workspaceId)}/connections/${encodeURIComponent(id)}/input`, null, {
    method: "POST",
    body: { code },
  })
export const setSubscriptionAccount = (workspaceId: string, subscription: string, email: string | null) =>
  apiRequest(`${path(workspaceId)}/subscriptions/${encodeURIComponent(subscription)}/account`, null, {
    method: "PUT",
    body: { email },
  })
/**
 * Decision 0128: Personal (only my work) or Team (anyone's in the workspace), by the subscription's owner. Decision
 * 0166 §3.1: or shared in some projects (`projectIds`).
 */
export const setPlatformSubscriptionScope = (
  workspaceId: string,
  subscription: string,
  scope: ProfileScope,
  projectIds: ReadonlyArray<string> = [],
) =>
  apiRequest(`${path(workspaceId)}/subscriptions/${encodeURIComponent(subscription)}/scope`, null, {
    method: "PUT",
    body: scope === "projects" ? { scope, projectIds } : { scope },
  })
export const disconnectPlatform = (workspaceId: string, profileId: string) =>
  apiRequest(`${path(workspaceId)}/subscriptions/${encodeURIComponent(profileId)}`, null, {
    method: "DELETE",
  })
export const configurePlatformGithub = (workspaceId: string, token: string) =>
  apiRequest(`${path(workspaceId)}/github`, null, { method: "PUT", body: { token } })

export const subscriptionDate = (at: number | null): string =>
  at === null ? "Unknown" : new Date(at).toLocaleDateString()
export const authorizationLink = (url: string | null): string | null => {
  if (!url) return null
  try {
    const parsed = new URL(url)
    return parsed.protocol === "https:" &&
      !parsed.username &&
      !parsed.password &&
      [
        "auth.openai.com",
        "chatgpt.com",
        "platform.openai.com",
        "claude.ai",
        "claude.com",
        "console.anthropic.com",
        "platform.claude.com",
      ].includes(parsed.hostname)
      ? parsed.href
      : null
  } catch {
    return null
  }
}

export const savePlatformProfile = (
  workspaceId: string,
  profile: Omit<PlatformExecutionProfile, "agent" | "revision">,
) => apiRequest(`${path(workspaceId)}/profiles`, null, { method: "PUT", body: profile })
export const deletePlatformProfile = (workspaceId: string, id: string) =>
  apiRequest(`${path(workspaceId)}/profiles/${encodeURIComponent(id)}`, null, { method: "DELETE" })

/** "Claude Max", "Codex", "Claude Code": a subscription as its owner knows it. */
export const subscriptionLabel = (agent: string | null, plan: string | null | undefined): string => {
  const product = agent === "codex" ? "Codex" : "Claude"
  if (!plan) return agent === "codex" ? "Codex subscription" : "Claude subscription"
  const tier = plan.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  return `${product} ${tier}`
}
